import { RouteName } from "constant/routeNames";
import React from "react";
import dayjs from "dayjs";
import { AiOutlineArrowLeft } from "react-icons/ai";
import { FaCheckCircle } from "react-icons/fa";
import { HiOutlineClock } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { MiniPackagesResponse } from "services/subscription/types";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { displayTimeFormat } from "utils/helperFuncs";

const PackageDetailsHeroCard = ({
  card,
  isSubscription,
  isLoading,
}: {
  card: MiniPackagesResponse;
  isSubscription: boolean;
  isLoading: boolean;
}) => {
  const navigate = useNavigate();

  const daysUntilExpiry = card?.service_end_date
    ? dayjs(card.service_end_date).diff(dayjs(), "day")
    : null;
  const isExpiringSoon =
    daysUntilExpiry !== null && daysUntilExpiry >= 0 && daysUntilExpiry <= 10;

  return (
    <div
      className="relative h-[380px] w-full overflow-hidden rounded-[18px] md:h-[460px]"
      style={{
        backgroundImage: `url("${card?.banner || card?.thumbnail}")`,
        backgroundPosition: "bottom center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-t from-[#06070C] via-[#06070C]/45 to-transparent" />
      <div className="absolute mt-10 ml-8 max-sm:mt-5 max-sm:ml-4 ">
        <button
          className="mb-5 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-black backdrop-blur-md transition-all duration-300 hover:scale-105 hover:bg-white max-sm:ml-[16px] max-lg:mt-[5px]"
          onClick={() => navigate(-1)}
        >
          <AiOutlineArrowLeft />
        </button>
      </div>

      <div className="absolute z-10 flex justify-center w-full text-center bottom-8 md:left-10">
        <div className="max-w-[560px]">
          <span className="inline-flex items-center rounded-full border border-[#F50537]/80 bg-[#0f1117]/55 px-5 py-2 text-xs font-semibold tracking-[0.12em] text-white backdrop-blur-md">
            {(card?.title ?? "").toUpperCase()} BUNDLE EXCLUSIVE
          </span>

          <h1 className="mt-3 text-4xl font-bold leading-tight tracking-tight text-white drop-shadow-2xl md:text-[46px]">
            {card?.title || "Kids Add-On"}
          </h1>

          <p
            className="mt-2 max-w-[470px] text-[13px] leading-5 text-white/80"
            data-tooltip-id="package-hero-description"
          >
            {(card?.description?.length ?? 0) > 190
              ? `${card.description.slice(0, 190)}...`
              : card?.description || ""}
          </p>

          {(card?.description?.length ?? 0) > 190 && (
            <div className="text-sm">
              <ReactTooltip
                id="package-hero-description"
                place="bottom"
                content={card?.description}
                style={{ width: "500px", zIndex: 50 }}
                delayShow={200}
              />
            </div>
          )}

          <div className="flex flex-col flex-wrap items-center justify-center gap-3 mt-5">
            {isExpiringSoon && (
              <div className="flex flex-row items-center justify-center">
                <p className="inline-flex items-center gap-2 rounded-full bg-[#F5A623] px-5 py-2 text-xs font-bold uppercase tracking-[0.06em] text-[#1a1305] shadow-[0_6px_18px_rgba(245,166,35,0.35)]">
                  <HiOutlineClock className="text-sm" />
                  {daysUntilExpiry === 0
                    ? "Package ends today"
                    : "Package will leave soon"}
                </p>
              </div>
            )}
            <div className="flex flex-row items-center justify-center gap-4">
              {isLoading ? (
                <div className="rounded-full h-14 w-44 bg-white/10 animate-pulse" />
              ) : isSubscription ? (
                <button
                  disabled
                  className="flex items-center gap-2 rounded-full border border-green-500/40 bg-[#141821]/80 px-10 py-4 text-base font-bold text-white shadow-[0_8px_24px_rgba(0,0,0,0.35)] backdrop-blur-md cursor-default"
                >
                  <FaCheckCircle className="text-lg text-green-500" />
                  Subscribed
                </button>
              ) : (
                <>
                  <button
                    className="rounded-full bg-[#F50537] px-10 py-4 text-base font-bold text-white shadow-lg shadow-[#F50537]/30 transition-all duration-300 hover:scale-[1.03] hover:brightness-110 hover:shadow-xl"
                    onClick={() =>
                      navigate(RouteName.SubscriptionPlanCheckout, {
                        state: { plan: card },
                      })
                    }
                  >
                    Subscribe to Watch
                  </button>
                  <div className="rounded-full border border-white/15 bg-[#141821]/80 px-10 pt-4 pb-[5px] text-white shadow-[0_8px_24px_rgba(0,0,0,0.35)] backdrop-blur-md">
                    <span className="text-[30px] font-bold leading-none">
                      ${card?.price || "4.99"}
                    </span>{" "}
                    <span className="text-[16px] font-normal text-white/75">
                      /{displayTimeFormat(card.plan_period_format)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackageDetailsHeroCard;
